import { useCallback, useMemo, useRef } from "react";

import { useBrandProfilesForResolutionQuery } from "src/graphql";
import { fetchBrandProfileFontFiles } from "src/pages/ad-studio/hooks/fetch-brand-profile-font-files";
import {
  buildPsdFontResolver,
  type PsdFontResolver,
} from "@hightouch/lib/ad-studio/psd-export/psd-font";

/** Cache key for the all-profiles index; profile ids are UUIDs, so no clash. */
const FALLBACK_CACHE_KEY = "*union*";

/**
 * Font resolvers scoped per brand profile: one shared index would collide when two profiles upload
 * the same family. No profile → the default, matching `useBrandDisclaimerDefaultsResolver`; no
 * default → the union.
 *
 * Uses `brandProfileFonts` rather than the raw `fonts` jsonb for each face's PostScript name, which
 * is parsed out of the binary server-side. Fetched lazily (most page views never export a PSD) and
 * memoized per profile as a promise, so a bulk download of one brand asks once.
 */
export function usePsdFontResolver(): (
  brandProfileId: string | null,
) => Promise<PsdFontResolver> {
  const { data } = useBrandProfilesForResolutionQuery();

  const profiles = useMemo(() => data?.brand_profiles ?? [], [data]);
  const cacheRef = useRef(new Map<string, Promise<PsdFontResolver>>());

  // Resolvers close over the profile list; drop them when it changes.
  const profilesKey = useMemo(
    () => profiles.map((profile) => profile.id).join(","),
    [profiles],
  );
  const cacheGeneration = useRef(profilesKey);
  if (cacheGeneration.current !== profilesKey) {
    cacheGeneration.current = profilesKey;
    cacheRef.current = new Map();
  }

  return useCallback(
    (brandProfileId) => {
      const defaultProfile = profiles.find((profile) => profile.is_default);
      const profile =
        (brandProfileId
          ? profiles.find((candidate) => candidate.id === brandProfileId)
          : undefined) ?? defaultProfile;

      const key = profile?.id ?? FALLBACK_CACHE_KEY;
      const cache = cacheRef.current;
      const cached = cache.get(key);
      if (cached !== undefined) return cached;

      // Annotated because `.catch` references `resolver` inside its own initializer.
      const resolver: Promise<PsdFontResolver> = fetchBrandProfileFontFiles(
        profile?.id,
      )
        .then(buildPsdFontResolver)
        // Evicted on failure, so a transient error doesn't poison every later export.
        .catch((err: unknown) => {
          if (cache.get(key) === resolver) cache.delete(key);
          throw err;
        });
      cache.set(key, resolver);
      return resolver;
    },
    [profiles],
  );
}
